<template>
    <table
        class="el-date-table"
        cellspacing="0"
        cellpadding="0"
        @mousedown.prevent
    >
        <thead>
            <tr>
                <th
                    v-for="w in weekHeaders"
                    :key="w.key"
                    class="el-date-table__weekday-name"
                    :class="{ weekends: w.isWeekend }"
                >
                    {{ w.label }}
                </th>
            </tr>
        </thead>
        <tbody>
            <tr
                v-for="(row, rIdx) in rows"
                :key="rIdx"
                class="el-date-table__row"
                :class="{ 'el-date-table__row--hidden': row.hidden }"
            >
                <td
                    v-for="cell in row.cells"
                    :key="cell.key"
                    :class="cell.classes"
                    @click="onPickCell(cell)"
                    @mouseenter="onHoverCell(cell)"
                >
                    <div :class="cell.innerClass">
                        <span>{{ cell.text }}</span>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { clearTime } from '@core/components/date-picker/util/index';

const props = withDefaults(
    defineProps<{
    modelValue?: Date | null;
    firstDayOfWeek?: number;
    panelDate?: Date | null;
    rangeStart?: Date | null;
    rangeEnd?: Date | null;
  }>(),
    {
        firstDayOfWeek: 1,
        modelValue: null,
        rangeStart: null,
        panelDate: null,
        rangeEnd: null,
    },
);

const emit = defineEmits<{
  (e: 'pick', d: Date): void;
  (e: 'update:modelValue', d: Date): void;
  (e: 'hover', d: Date): void;
}>();

const panelDate = computed(() => props.panelDate ?? new Date());
const today = clearTime(new Date());
const selected = computed(() => (props.modelValue ? clearTime(props.modelValue) : null));

const weekHeaders = computed(() => {
    const labels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const shift = props.firstDayOfWeek % 7;
    const ordered = labels.slice(shift).concat(labels.slice(0, shift));
    const mapIdx = (idx: number) => (shift + idx) % 7;
    return ordered.map((label, i) => ({
        key: `${i}-${label}`,
        label,
        isWeekend: [0, 6].includes(mapIdx(i)),
    }));
});

function startOfGrid(year: number, month: number, firstDayOfWeek: number) {
    const fd = firstDayOfWeek === 7 ? 0 : firstDayOfWeek;
    const first = new Date(year, month, 1);
    const delta = (7 + (first.getDay() - fd)) % 7;
    return new Date(year, month, 1 - delta);
}
function isSameDay(a?: Date | null, b?: Date | null) {
    if (!a || !b) return false;
    return clearTime(a).getTime() === clearTime(b).getTime();
}

const rows = computed(() => {
    const year = panelDate.value.getFullYear();
    const month = panelDate.value.getMonth();

    const start = startOfGrid(year, month, props.firstDayOfWeek);
    const out: Array<{ hidden?: boolean; cells: any[] }> = [];

    let cursor = new Date(start);
    for (let r = 0; r < 6; r++) {
        const cells: any[] = [];
        for (let c = 0; c < 7; c++) {
            const d = clearTime(cursor);

            const inPrev =
        d.getFullYear() < year || (d.getFullYear() === year && d.getMonth() < month);
            const inNext =
        d.getFullYear() > year || (d.getFullYear() === year && d.getMonth() > month);
            const inMonth = !inPrev && !inNext;

            const weekend = d.getDay() === 0 || d.getDay() === 6;

            const isStart = props.rangeStart ? isSameDay(d, props.rangeStart) : false;
            const isEnd = props.rangeEnd ? isSameDay(d, props.rangeEnd) : false;
            const isInRange =
        props.rangeStart && props.rangeEnd
            ? d.getTime() >= clearTime(props.rangeStart).getTime() &&
            d.getTime() <= clearTime(props.rangeEnd).getTime()
            : false;

            const classes = {
                'prev-month': inPrev,
                'next-month': inNext,
                available: inMonth,
                weekends: weekend,
                today: inMonth && isSameDay(d, today),
                current: isSameDay(d, selected.value),
                'in-range': Boolean(isInRange),
                'start-date': isStart,
                'end-date': isEnd,
                'start-date-consecutive':
          isStart && props.rangeEnd && d.getTime() < props.rangeEnd.getTime(),
            };

            cells.push({
                key: d.toISOString(),
                date: d,
                text: d.getDate(),
                classes,
                innerClass: isStart ? 'el-date-table--start' : isEnd ? 'el-date-table--end' : '',
            });

            cursor.setDate(cursor.getDate() + 1);
        }
        out.push({ cells });
    }

    const last = out[5];
    if (last && last.cells.every((cell: any) => cell.classes['next-month'])) {
        last.hidden = true;
    }

    return out;
});

function onPickCell(cell: any) {
    if (!cell.classes.available) return;
    emit('pick', cell.date);
    emit('update:modelValue', cell.date);
}
function onHoverCell(cell: any) {
    emit('hover', cell.date);
}
</script>
